// import UUIDGenerator from 'shortid';
import { nanoid } from 'nanoid';

/**
 * @constant
 * @type {number}
 */
const ID_LENGTH = 6;
/**
 * @constant
 * @type {number}
 */
const RESEND_DELAY = 5 * 1000;
/**
 * @constant
 * @type {Object}
 */
const CookiesKeys = {
  GA_ID: '_ga',
  REFERRER: 'referrer',
  TRAFFIC_SOURCE: '__utmz',
  UUID: 'uuid',
};
/**
 * @constant
 * @type {Object}
 */
const LocalStorageKeys = {
  EVENTS: 'ccx-events',
  GEOPOSITION: 'lt-geoposition',
  UUID: 'ccx-uuid',
};

/**
 * Трекер событий для Travel Analytics
 * @class
 * @see http://lvtv.me/HitStorage
 */
class HitStorageTracker {
  /**
   * @param {string} name
   * @returns {string}
   * @static
   */
  static getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 1) {
      return '';
    }

    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }

    /**
     * Если у пользователя несколько utmz кук, оставляем одну
     */
    if (name === CookiesKeys.TRAFFIC_SOURCE) {
      parts.shift();
      const excludeLTutmz = parts.filter(item => !item.includes('level.travel'));
      const utmz = excludeLTutmz[0] || parts[0];

      return utmz.split(';').shift();
    }

    return '';
  }

  /**
   * @returns {string}
   * @static
   */
  static getGoogleClientId() {
    try {
      // Попытка взять из куки.
      let id = HitStorageTracker.getCookie(CookiesKeys.GA_ID);
      if (id) {
        id = id.split('.').slice(-2).join('.');
        return id;
      }

      // Попытка взять из объекта ga, если кука по какой-то причине не доступна.
      if (window.ga && typeof window.ga.getAll === 'function') {
        const gaTrackers = window.ga.getAll();

        if (gaTrackers) {
          id = gaTrackers[0].get('clientId');
        }
      }

      return id;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  /**
   * @returns {Object}
   * @static
   */
  static getTrafficSourceData() {
    const source = HitStorageTracker.getCookie(CookiesKeys.TRAFFIC_SOURCE);
    if (!source) {
      return null;
    }

    /**
     * @type {Object}
     * @property {string} utmccn - Campaign Name.
     * @property {string} utmcct - Campaign Content.
     * @property {string} utmcmd - Campaign Medium.
     * @property {string} utmcsr - Campaign Source.
     * @property {string} utmctr - Campaign Term.
     */
    const values = {};
    const z = source.split('.');
    if (z.length < 4) {
      return null;
    }

    const params = z[4].split('|');
    params.forEach((param) => {
      const pair = param.split('=');
      if (pair.length < 2) {
        return;
      }

      values[pair[0]] = pair[1].replace('(', '').replace(')', '');
    });

    return {
      campaign: values.utmccn || '',
      content: values.utmcct || '',
      medium: values.utmcmd || '',
      referrer: document.referrer || HitStorageTracker.getCookie(CookiesKeys.REFERRER) || '',
      source: values.utmcsr || '',
      term: values.utmctr || '',
    };
  }

  static getUUID() {
    let uuid = localStorage.getItem(LocalStorageKeys.UUID);
    if (!uuid) {
      uuid = nanoid();
      localStorage.setItem(LocalStorageKeys.UUID, uuid);
    }

    return uuid;
  }

  /**
   * @param {*} id
   * @returns {boolean}
   * @static
   */
  static isValidId(id) {
    return typeof id === 'string' && id.length === ID_LENGTH;
  }

  /**
   * @returns {Tracker}
   */
  constructor(trackerId) {
    this.state = {
      events: {},
      id: 0,
      interval: null,
      isGAWaiting: true,
      location: {
        city: '',
        latitude: null,
        longitude: null,
      },
      trackerId: null,
      uuid: HitStorageTracker.getUUID(),
    };

    this.resend = this.resend.bind(this);

    this.init(trackerId);

    this.loadEventsByLocalStorage();
    this.addHandlers();

    this.getGeolocation();
    this.startAutoSending();
  }

  /**
   * Добавление события в трекер и отсылка его на сервер.
   * @param {string} type
   * @param {string} name
   * @param {Object} [data]
   */
  add(type, name, data) {
    try {
      if (!HitStorageTracker.isValidId(this.state.trackerId)) {
        throw new Error('Необходимо задать идентификатор трекера.');
      }

      if (typeof type !== 'string' || typeof name !== 'string') {
        throw new Error(`Параметры type и name должны быть строкового типа, а приходят type: ${JSON.stringify(type)}, name: ${JSON.stringify(name)}.`);
      }

      const event = this.createEvent(type, name, data);
      this.state.id = this.state.id + 1;
      this.state.events[this.state.id] = event;
      this.send(this.state.id);
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * @private
   */
  addHandlers() {
    window.addEventListener('unload', () => {
      const events = Object.values(this.state.events).filter(event => !event.isSending);
      localStorage.setItem('ccx-events', JSON.stringify(events));
    });
  }

  /**
   * Возвращает координаты с 4 знаками после запятой
   * @param {string} location lat/long
   * @returns {number} 55.7645
   */
  static formatGeoposition(location) {
    if (!location) {
      return null;
    }

    const formatted = parseFloat(location).toFixed(4);
    return Number(formatted);
  }

  /**
   * Получение геопозиции.
   * @returns {Object}
   * @private
   */
  getGeolocation() {
    const geo = JSON.parse(localStorage.getItem(LocalStorageKeys.GEOPOSITION));

    if (!geo) {
      return;
    }

    const { city, location } = geo.payload;

    this.state.location = {
      city: city.name_en || '',
      latitude: HitStorageTracker.formatGeoposition(location.lat),
      longitude: HitStorageTracker.formatGeoposition(location.long),
    };
  }

  /**
   * Сборка данных для отправки на сервер.
   * @param {string} type
   * @param {string} name
   * @param {Object} [data]
   * @returns {Object}
   * @private
   */
  createEvent(type, name, data) {
    return {
      hit: {
        context: {
          locale: navigator.language,
          location: this.state.location,
          page: {
            domain: window.location.hostname,
            path: window.location.pathname,
            title: document.title,
            url: window.location.href,
          },
          timezone: `GMT${new Date().toString().split('GMT')[1]}`,
          traffic_source: HitStorageTracker.getTrafficSourceData(),
        },
        created_at: Math.floor(Date.now() / 1000),
        hit_type: type,
        name,
        properties: Object.assign({}, {
          affiliate: new window.URL(window.location).searchParams.get('aflt') || '',
        }, data),
        tracker_id: this.state.trackerId,
        user: {
          client_id: window.current_client ? window.current_client.id : null,
          google_client_id: HitStorageTracker.getGoogleClientId(),
          user_id: HitStorageTracker.getCookie(CookiesKeys.UUID),
        },
      },
      isSending: false,
    };
  }

  /**
   * Инициализация трекера.
   * @param {string} trackerId
   */
  init(trackerId) {
    try {
      if (!HitStorageTracker.isValidId(trackerId)) {
        throw new Error('Задан неверный идентификатор трекера.');
      }

      this.state.trackerId = trackerId;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * @private
   */
  loadEventsByLocalStorage() {
    const events = JSON.parse(localStorage.getItem(LocalStorageKeys.EVENTS));
    if (!events || !events.length) {
      return;
    }

    events.forEach((event) => {
      this.state.id = this.state.id + 1;
      this.state.events[this.state.id] = event;
    });
  }

  /**
   * Попытка повторной отправки недоставленных событий.
   * @private
   */
  resend() {
    const { events } = this.state;
    const keys = Object.keys(events).filter(key => !events[key].isSending);
    if (!keys.length) {
      return;
    }

    this.send(keys);
  }

  /**
   * Отсылка события на сервер.
   * @param {number|Array} keyOrKeys
   * @private
   */
  send(keyOrKeys) {
    const keys = typeof keyOrKeys === 'number' ? [keyOrKeys] : keyOrKeys;

    const events = [];
    keys.forEach((key) => {
      const event = this.state.events[key];
      if (event && !event.isSending) {
        event.isSending = true;
        events.push(event);
      }
    });

    if (!events.length) {
      return;
    }

    fetch(API_URL, {
      body: JSON.stringify({
        hits: events.map(event => event.hit),
        uuid: this.state.uuid,
        new_uuid_format: true,
      }),
      cache: 'no-cache',
      credentials: 'include',
      method: 'POST',
    })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Go to catch statement!');
        }

        keys.forEach(key => delete this.state.events[key]);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  /**
   * Запуск повторной отправки недоставленных событий.
   * @private
   */
  startAutoSending() {
    clearInterval(this.state.interval);
    this.state.interval = setInterval(this.resend, RESEND_DELAY);
  }
}

export default HitStorageTracker;
